import React from 'react'

import { Box, WizardStep, WizardStepRenderParams } from '@factorialco/deprecated-design-system'

import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import i18n from 'modules/core/lib/i18n'

import { WizardData } from 'modules/shift_management/components/ShiftManagement/CopyPeriodWizard/utils'
import {
  CopyType,
  CopyTypeEnum,
  DateValues,
  diffTimes,
  isValidPeriodOrigin,
} from 'modules/shift_management/components/ShiftManagement/CopyPeriodWizard/utils'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import CopyPeriodForm from 'modules/shift_management/lib/forms/CopyPeriodForm'

import CopyPeriod from './CopyPeriod'

const OriginStep = ({ data, onDataChanged }: WizardStepRenderParams<WizardData>) => {
  const { startDateSchedule, endDateSchedule, startDateCoverage, endDateCoverage, copyType } = data

  const [{ view }] = useSchedule()

  const copySchedulePeriodForm = useLazyRef(
    () =>
      new CopyPeriodForm({
        start_at: startDateSchedule ? startDateSchedule : view.startAt,
        end_at: endDateSchedule ? endDateSchedule : view.endAt,
        destination_start_at: '',
      })
  ).current

  const copyCoveragePeriodForm = useLazyRef(
    () =>
      new CopyPeriodForm({
        start_at: startDateCoverage ? startDateCoverage : view.startAt,
        end_at: endDateCoverage ? endDateCoverage : view.endAt,
        destination_start_at: '',
      })
  ).current

  const diffScheduleDays = diffTimes(copySchedulePeriodForm)
  const diffCoverageDays = diffTimes(copyCoveragePeriodForm)

  const [daysSchedule, setDaysSchedule] = React.useState<number>(diffScheduleDays)
  const [daysCoverage, setDaysCoverage] = React.useState<number>(diffCoverageDays)
  const [copyTypePeriod, setCopyTypePeriod] = React.useState<CopyType>(copyType)

  const onSelectCopyType = (type: CopyType) => {
    const newCopyType = copyTypePeriod === type ? undefined : type
    setCopyTypePeriod(newCopyType)
    onDataChanged({ ...data, copyType: newCopyType })
  }

  const onChangePeriod = (values: DateValues, form: CopyPeriodForm) => {
    form.get('start_at').set(values[0])
    form.get('end_at').set(values[1])

    const newDiffScheduleDays = diffTimes(copySchedulePeriodForm)
    const newDiffCoverageDays = diffTimes(copyCoveragePeriodForm)

    setDaysSchedule(newDiffScheduleDays)
    setDaysCoverage(newDiffCoverageDays)

    onDataChanged({
      ...data,
      startDateSchedule: copySchedulePeriodForm.get('start_at').value,
      endDateSchedule: copySchedulePeriodForm.get('end_at').value,
      daysSchedule: newDiffScheduleDays,
      startDateCoverage: copyCoveragePeriodForm.get('start_at').value,
      endDateCoverage: copyCoveragePeriodForm.get('end_at').value,
      daysCoverage: newDiffCoverageDays,
    })
  }

  const scheduleTitle = i18n.t('shift_management.copy_schedule_period.origin_step.schedule.title')
  const scheduleDescription = i18n.t(
    'shift_management.copy_schedule_period.origin_step.schedule.description'
  )

  const coverageTitle = i18n.t('shift_management.copy_schedule_period.origin_step.coverage.title')
  const coverageDescription = i18n.t(
    'shift_management.copy_schedule_period.origin_step.coverage.description'
  )

  return (
    <Box flexDirection="column" gap="s12">
      <CopyPeriod
        onChangePeriod={(values) => onChangePeriod(values, copySchedulePeriodForm)}
        onCopyPeriod={() => onSelectCopyType(CopyTypeEnum.schedule)}
        isSelected={copyType === CopyTypeEnum.schedule}
        copyPeriodForm={copySchedulePeriodForm}
        title={scheduleTitle}
        description={scheduleDescription}
        days={daysSchedule}
      />
      <CopyPeriod
        onChangePeriod={(values) => onChangePeriod(values, copyCoveragePeriodForm)}
        onCopyPeriod={() => onSelectCopyType(CopyTypeEnum.coverage)}
        isSelected={copyType === CopyTypeEnum.coverage}
        copyPeriodForm={copyCoveragePeriodForm}
        title={coverageTitle}
        description={coverageDescription}
        days={daysCoverage}
      />
    </Box>
  )
}

export const originStep: () => WizardStep<WizardData> = () => ({
  title: i18n.t('shift_management.copy_schedule_period.origin_step.title_step'),
  nextText: i18n.t('shift_management.copy_schedule_period.origin_step.next_text'),
  render: (props) => <OriginStep {...props} />,
  isCompleted: (data) =>
    !!(
      data.copyType &&
      ((data.startDateSchedule && data.endDateSchedule && isValidPeriodOrigin(data.daysSchedule)) ||
        (data.startDateCoverage && data.endDateCoverage && isValidPeriodOrigin(data.daysCoverage)))
    ),
})
